import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const AVAILABLE_MODULES = [
  { id: 'PATHOLOGY', name: 'Pathology & Lab Diagnostics', icon: '🔬', desc: 'Blood examination, analyzers, cell counts, differential tests' },
  { id: 'RADIOLOGY', name: 'Radiology & Imaging', icon: '🩻', desc: 'X-Ray, Ultrasound, CT/MRI reporting and DICOM integration' },
  { id: 'PHLEBOTOMY', name: 'Phlebotomy & Accessioning', icon: '🩸', desc: 'Barcode collection, sample accessioning, specimen rejection' },
  { id: 'BILLING', name: 'Corporate & Patient Billing', icon: '💳', desc: 'Panel tariffs, rate lists, co-pay settlements, receipt generation' },
  { id: 'B2B_PORTAL', name: 'B2B Client Enterprise Portal', icon: '🏢', desc: 'Hospital and corporate direct order entry and result dispatch' }
];

const SaasLifecycleAndControls = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [statusInfo, setStatusInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Lock / Unlock Modal
  const [showLockModal, setShowLockModal] = useState(false);
  const [lockAction, setLockAction] = useState('lock'); // 'lock' | 'unlock'
  const [lockReason, setLockReason] = useState('Administrative freeze due to billing reconciliation');
  const [submittingLock, setSubmittingLock] = useState(false);

  // Safe Offboarding Modal
  const [showOffboardModal, setShowOffboardModal] = useState(false);
  const [offboardReason, setOffboardReason] = useState('Customer contract term completed; archiving data under 90-day retention policy');
  const [retentionDays, setRetentionDays] = useState(90);
  const [submittingOffboard, setSubmittingOffboard] = useState(false);

  // Module Toggle State
  const [disabledModules, setDisabledModules] = useState([]);
  const [savingModules, setSavingModules] = useState(false);

  // Tenant Quota Limits & Delinquency Governance State
  const [quotaForm, setQuotaForm] = useState({
    maxUsers: 25,
    maxRegistrationsPerMonth: 1000,
    delinquencyAction: 'SOFT_LOCK_READONLY',
    delinquencyStatus: 'CURRENT',
    tenantStatus: 'ACTIVE'
  });
  const [savingQuotas, setSavingQuotas] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);
      try {
        const res = await saasPlatformService.getTenants();
        const list = res.data || res || [];
        setTenants(list);
        if (list.length > 0) {
          const defaultTenant = list.find(t => t.tenantId === 9111) || list[0];
          setSelectedTenantId(defaultTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to load tenants', err);
        setError('Failed to load tenants list.');
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, []);

  const loadLifecycleStatus = async (tenantId) => {
    if (!tenantId) return;
    setStatusLoading(true);
    setError(null);
    try {
      const res = await saasPlatformService.getTenantLifecycleStatus(tenantId);
      const data = res.data || res;
      setStatusInfo(data);
      setDisabledModules(data.disabledModules || []);
      setQuotaForm({
        maxUsers: data.maxUsers || 25,
        maxRegistrationsPerMonth: data.maxRegistrationsPerMonth || 1000,
        delinquencyAction: data.delinquencyAction || 'SOFT_LOCK_READONLY',
        delinquencyStatus: data.delinquencyStatus || 'CURRENT',
        tenantStatus: data.lifecycleStatus || 'ACTIVE'
      });
    } catch (err) {
      console.error('Failed to load lifecycle status', err);
      setError('Failed to load lifecycle status.');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSaveQuotas = async (e) => {
    if (e) e.preventDefault();
    if (!selectedTenantId) return;
    setSavingQuotas(true);
    setError(null);
    try {
      await saasPlatformService.updateTenantGovernanceQuotas(selectedTenantId, {
        maxUsers: parseInt(quotaForm.maxUsers, 10),
        maxRegistrationsPerMonth: parseInt(quotaForm.maxRegistrationsPerMonth, 10),
        delinquencyAction: quotaForm.delinquencyAction,
        delinquencyStatus: quotaForm.delinquencyStatus,
        status: quotaForm.tenantStatus
      });
      setSuccess(`Tenant #${selectedTenantId} quotas and delinquency enforcement rules updated successfully!`);
      loadLifecycleStatus(selectedTenantId);
    } catch (err) {
      console.error('Failed to update tenant quotas', err);
      setError(err?.response?.data?.message || 'Failed to update tenant quotas.');
    } finally {
      setSavingQuotas(false);
    }
  };

  useEffect(() => {
    if (selectedTenantId) {
      loadLifecycleStatus(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleOpenLock = (action) => {
    setLockAction(action);
    setLockReason(action === 'lock' ? 'Administrative freeze due to billing reconciliation' : 'Administrative restriction lifted by Platform Administrator');
    setShowLockModal(true);
  };

  const handleLockSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSubmittingLock(true);
    setError(null);
    try {
      if (lockAction === 'lock') {
        await saasPlatformService.lockTenant(selectedTenantId, { reason: lockReason });
        setSuccess(`Tenant #${selectedTenantId} administrative lock enabled. All login attempts fail closed.`);
      } else {
        await saasPlatformService.unlockTenant(selectedTenantId, { reason: lockReason });
        setSuccess(`Tenant #${selectedTenantId} administrative lock lifted successfully.`);
      }
      setShowLockModal(false);
      loadLifecycleStatus(selectedTenantId);
    } catch (err) {
      console.error('Lock/unlock failed', err);
      setError(err?.response?.data?.message || 'Lifecycle operation failed.');
    } finally {
      setSubmittingLock(false);
    }
  };

  const handleToggleModule = (moduleId) => {
    setDisabledModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(m => m !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  const handleSaveModules = async () => {
    if (!selectedTenantId) return;
    setSavingModules(true);
    setError(null);
    try {
      await saasPlatformService.updateTenantModules(selectedTenantId, {
        disabledModules,
        reason: 'Updated module access permissions via Control Center'
      });
      setSuccess(`Module entitlements updated successfully!`);
      loadLifecycleStatus(selectedTenantId);
    } catch (err) {
      console.error('Failed to update modules', err);
      setError(err?.response?.data?.message || 'Failed to update modules.');
    } finally {
      setSavingModules(false);
    }
  };

  const handleOffboardSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setSubmittingOffboard(true);
    setError(null);
    try {
      await saasPlatformService.offboardTenant(selectedTenantId, offboardReason, parseInt(retentionDays, 10));
      setSuccess(`Tenant #${selectedTenantId} marked for controlled offboarding with a ${retentionDays}-day legal retention hold.`);
      setShowOffboardModal(false);
      loadLifecycleStatus(selectedTenantId);
    } catch (err) {
      console.error('Offboard failed', err);
      setError(err?.response?.data?.message || 'Offboarding request failed.');
    } finally {
      setSubmittingOffboard(false);
    }
  };

  const currentTenantObj = tenants.find(t => t.tenantId === Number(selectedTenantId));

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>🛡️</span> Tenant Lifecycle, Lock &amp; Service Controls
          </h1>
          <p>Administrative Freeze, Granular Service Module Toggles &amp; Safe Offboarding Safeguards</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div><strong>Lifecycle Alert:</strong> {error}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="saas-success-box">
          <div><strong>Notice:</strong> {success}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Tenant Selector */}
      <div className="saas-card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Select Client:</label>
            <select
              className="saas-select"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              style={{ minWidth: '320px', fontWeight: 600 }}
            >
              {tenants.map(t => (
                <option key={t.tenantId} value={t.tenantId}>
                  #{t.tenantId} — {t.tenantCode} ({t.organizationName})
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {statusInfo?.adminLocked ? (
              <button className="saas-btn saas-btn-success" onClick={() => handleOpenLock('unlock')}>
                🔓 Unlock Tenant Access
              </button>
            ) : (
              <button className="saas-btn saas-btn-danger" onClick={() => handleOpenLock('lock')}>
                🔒 Administrative Lock
              </button>
            )}
            <button className="saas-btn saas-btn-secondary" onClick={() => setShowOffboardModal(true)}>
              📦 Safe Offboarding
            </button>
          </div>
        </div>
      </div>

      {/* Status Summary Banner */}
      {statusInfo && (
        <div className="saas-card" style={{ marginBottom: '1.5rem', borderLeft: statusInfo.adminLocked ? '4px solid #ef4444' : '4px solid #10b981' }}>
          <div className="saas-card-body" style={{ padding: '1.25rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>LIFECYCLE STATUS</span>
                <div style={{ marginTop: '0.25rem' }}>
                  <span className={`saas-badge ${statusInfo.status === 'ACTIVE' ? 'saas-badge-active' : 'saas-badge-suspended'}`}>
                    ● {statusInfo.status}
                  </span>
                </div>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>ADMINISTRATIVE LOCK</span>
                <div style={{ marginTop: '0.25rem' }}>
                  {statusInfo.adminLocked ? (
                    <span className="saas-badge saas-badge-suspended">
                      🔒 LOCKED: {statusInfo.adminLockReason || 'Policy Violation'}
                    </span>
                  ) : (
                    <span className="saas-badge saas-badge-active">
                      🔓 UNLOCKED (Normal Operations)
                    </span>
                  )}
                </div>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>RETENTION STATUS</span>
                <div style={{ marginTop: '0.25rem' }}>
                  {statusInfo.retentionHoldUntil ? (
                    <span className="saas-badge saas-badge-warning">
                      ⚠️ Hold until {new Date(statusInfo.retentionHoldUntil).toLocaleDateString()}
                    </span>
                  ) : (
                    <span style={{ color: '#047857', fontWeight: 600, fontSize: '0.875rem' }}>Active Client</span>
                  )}
                </div>
              </div>

              <div>
                <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600 }}>DISABLED SERVICES</span>
                <div style={{ marginTop: '0.25rem' }}>
                  <span style={{ fontWeight: 600, color: disabledModules.length > 0 ? '#b91c1c' : '#047857' }}>
                    {disabledModules.length > 0 ? `${disabledModules.length} Modules Disabled` : 'All Services Online'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tenant Quotas, User Limits & Delinquency Governance */}
      <div className="saas-card" style={{ marginBottom: '1.5rem', border: '1px solid #cbd5e1' }}>
        <div className="saas-card-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <div>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>⚙️</span> Tenant Quotas, Operational Limits &amp; Overdue Policy
            </h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Direct control over allowed user accounts, monthly registration limits, and payment overdue penalties
            </span>
          </div>
          <button
            className="saas-btn saas-btn-primary"
            onClick={handleSaveQuotas}
            disabled={savingQuotas}
            style={{ fontWeight: 600, boxShadow: '0 2px 4px rgba(37,99,235,0.2)' }}
          >
            {savingQuotas ? 'Saving Policies...' : '💾 Save Governance & Quotas'}
          </button>
        </div>

        <div className="saas-card-body" style={{ padding: '1.5rem' }}>
          {/* Live Utilization Telemetry Bars */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem', marginBottom: '1.75rem' }}>
            {/* User Quota Meter */}
            <div style={{ background: '#f1f5f9', padding: '1rem 1.25rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>👥 Active User Accounts</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>
                  {statusInfo?.currentUsersCount ?? 1} / {quotaForm.maxUsers >= 999999 ? '∞ Unlimited' : quotaForm.maxUsers}
                </span>
              </div>
              {quotaForm.maxUsers < 999999 && (
                <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, Math.round(((statusInfo?.currentUsersCount ?? 1) / quotaForm.maxUsers) * 100))}%`,
                      background: ((statusInfo?.currentUsersCount ?? 1) / quotaForm.maxUsers) > 0.9 ? '#ef4444' : (((statusInfo?.currentUsersCount ?? 1) / quotaForm.maxUsers) > 0.75 ? '#f59e0b' : '#10b981'),
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              )}
              <span style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.35rem', display: 'block' }}>
                Pathologists, lab techs, and billing desk accounts allowed in tenant database
              </span>
            </div>

            {/* Monthly Registration Quota Meter */}
            <div style={{ background: '#f1f5f9', padding: '1rem 1.25rem', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#475569' }}>📋 Monthly Patient Registrations</span>
                <span style={{ fontSize: '0.875rem', fontWeight: 700, color: '#0f172a' }}>
                  {statusInfo?.currentRegistrationsCount ?? 0} / {quotaForm.maxRegistrationsPerMonth >= 999999 ? '∞ Unlimited' : quotaForm.maxRegistrationsPerMonth.toLocaleString()}
                </span>
              </div>
              {quotaForm.maxRegistrationsPerMonth < 999999 && (
                <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${Math.min(100, Math.round(((statusInfo?.currentRegistrationsCount ?? 0) / quotaForm.maxRegistrationsPerMonth) * 100))}%`,
                      background: ((statusInfo?.currentRegistrationsCount ?? 0) / quotaForm.maxRegistrationsPerMonth) > 0.9 ? '#ef4444' : (((statusInfo?.currentRegistrationsCount ?? 0) / quotaForm.maxRegistrationsPerMonth) > 0.75 ? '#f59e0b' : '#10b981'),
                      transition: 'width 0.3s ease'
                    }}
                  />
                </div>
              )}
              <span style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.35rem', display: 'block' }}>
                New patient visits / lab work orders permitted per calendar month
              </span>
            </div>
          </div>

          {/* Form Configuration Inputs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
            {/* 1. Max Users Limit */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem', color: '#1e293b' }}>
                Max User Accounts Limit:
              </label>
              <input
                type="number"
                min="1"
                className="saas-input"
                value={quotaForm.maxUsers}
                onChange={(e) => setQuotaForm(prev => ({ ...prev, maxUsers: parseInt(e.target.value, 10) || 1 }))}
                style={{ width: '100%', marginBottom: '0.5rem', fontWeight: 600 }}
              />
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                {[5, 10, 25, 50, 100, 999999].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setQuotaForm(prev => ({ ...prev, maxUsers: num }))}
                    style={{
                      padding: '0.2rem 0.5rem',
                      fontSize: '0.72rem',
                      borderRadius: '4px',
                      border: '1px solid #cbd5e1',
                      background: quotaForm.maxUsers === num ? '#2563eb' : '#fff',
                      color: quotaForm.maxUsers === num ? '#fff' : '#475569',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {num === 999999 ? 'Unlimited' : `${num} Users`}
                  </button>
                ))}
              </div>
            </div>

            {/* 2. Max Registrations Limit */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem', color: '#1e293b' }}>
                Max Monthly Registrations Limit:
              </label>
              <input
                type="number"
                min="100"
                step="100"
                className="saas-input"
                value={quotaForm.maxRegistrationsPerMonth}
                onChange={(e) => setQuotaForm(prev => ({ ...prev, maxRegistrationsPerMonth: parseInt(e.target.value, 10) || 100 }))}
                style={{ width: '100%', marginBottom: '0.5rem', fontWeight: 600 }}
              />
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                {[500, 1000, 2500, 5000, 10000, 999999].map(num => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setQuotaForm(prev => ({ ...prev, maxRegistrationsPerMonth: num }))}
                    style={{
                      padding: '0.2rem 0.5rem',
                      fontSize: '0.72rem',
                      borderRadius: '4px',
                      border: '1px solid #cbd5e1',
                      background: quotaForm.maxRegistrationsPerMonth === num ? '#2563eb' : '#fff',
                      color: quotaForm.maxRegistrationsPerMonth === num ? '#fff' : '#475569',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {num === 999999 ? 'Unlimited' : `${num.toLocaleString()} / mo`}
                  </button>
                ))}
              </div>
            </div>

            {/* 3. Tenant Status Switch */}
            <div>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.4rem', color: '#1e293b' }}>
                Operational Status:
              </label>
              <select
                className="saas-select"
                value={quotaForm.tenantStatus}
                onChange={(e) => setQuotaForm(prev => ({ ...prev, tenantStatus: e.target.value }))}
                style={{ width: '100%', fontWeight: 600 }}
              >
                <option value="ACTIVE">🟢 ACTIVE (Fully Operational)</option>
                <option value="SUSPENDED">🟠 SUSPENDED (Temporary Hold)</option>
                <option value="DEACTIVATED">🔴 DEACTIVATED (Decommissioned)</option>
              </select>
              <span style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '0.35rem', display: 'block' }}>
                Change lifecycle state directly without terminating underlying storage
              </span>
            </div>
          </div>

          {/* 4. Payment Overdue / Delinquency Policy Section */}
          <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <div>
                <strong style={{ fontSize: '0.9375rem', color: '#0f172a' }}>
                  💳 Overdue Payment Delinquency Policy (पेमेंट बकाया एक्शन)
                </strong>
                <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8125rem', color: '#64748b' }}>
                  Defines exactly what happens to this tenant's clinic when invoice payments are past due date
                </p>
              </div>

              {/* Status override indicator */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Billing Status:</span>
                <select
                  className="saas-select"
                  value={quotaForm.delinquencyStatus}
                  onChange={(e) => setQuotaForm(prev => ({ ...prev, delinquencyStatus: e.target.value }))}
                  style={{ fontSize: '0.8125rem', padding: '0.3rem 0.6rem' }}
                >
                  <option value="CURRENT">CURRENT (Payment Up to Date)</option>
                  <option value="GRACE_PERIOD">GRACE_PERIOD (Due within 7 Days)</option>
                  <option value="OVERDUE">OVERDUE (Payment Past Due)</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.85rem' }}>
              {/* Option A: WARN_ONLY */}
              <div
                onClick={() => setQuotaForm(prev => ({ ...prev, delinquencyAction: 'WARN_ONLY' }))}
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  border: quotaForm.delinquencyAction === 'WARN_ONLY' ? '2px solid #eab308' : '1px solid #cbd5e1',
                  background: quotaForm.delinquencyAction === 'WARN_ONLY' ? '#fefce8' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <input
                    type="radio"
                    name="delinquencyAction"
                    value="WARN_ONLY"
                    checked={quotaForm.delinquencyAction === 'WARN_ONLY'}
                    onChange={() => {}}
                  />
                  <strong style={{ fontSize: '0.875rem', color: '#854d0e' }}>🟡 WARN_ONLY (Alert Banners)</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#713f12', lineHeight: 1.4 }}>
                  Displays persistent amber warning banners on tenant screens requesting settlement. Does <strong>not</strong> stop clinical registrations or lab processing.
                </p>
              </div>

              {/* Option B: SOFT_LOCK_READONLY */}
              <div
                onClick={() => setQuotaForm(prev => ({ ...prev, delinquencyAction: 'SOFT_LOCK_READONLY' }))}
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  border: quotaForm.delinquencyAction === 'SOFT_LOCK_READONLY' ? '2px solid #f97316' : '1px solid #cbd5e1',
                  background: quotaForm.delinquencyAction === 'SOFT_LOCK_READONLY' ? '#fff7ed' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <input
                    type="radio"
                    name="delinquencyAction"
                    value="SOFT_LOCK_READONLY"
                    checked={quotaForm.delinquencyAction === 'SOFT_LOCK_READONLY'}
                    onChange={() => {}}
                  />
                  <strong style={{ fontSize: '0.875rem', color: '#c2410c' }}>🟠 SOFT_LOCK_READONLY (Protected)</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#9a3412', lineHeight: 1.4 }}>
                  <strong>Blocks new patient registrations and test booking.</strong> Doctors and technicians can still view and print existing patient reports.
                </p>
              </div>

              {/* Option C: HARD_LOCKOUT */}
              <div
                onClick={() => setQuotaForm(prev => ({ ...prev, delinquencyAction: 'HARD_LOCKOUT' }))}
                style={{
                  padding: '1rem',
                  borderRadius: '8px',
                  border: quotaForm.delinquencyAction === 'HARD_LOCKOUT' ? '2px solid #ef4444' : '1px solid #cbd5e1',
                  background: quotaForm.delinquencyAction === 'HARD_LOCKOUT' ? '#fef2f2' : '#fff',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
                  <input
                    type="radio"
                    name="delinquencyAction"
                    value="HARD_LOCKOUT"
                    checked={quotaForm.delinquencyAction === 'HARD_LOCKOUT'}
                    onChange={() => {}}
                  />
                  <strong style={{ fontSize: '0.875rem', color: '#b91c1c' }}>🔴 HARD_LOCKOUT (Strict Lock)</strong>
                </div>
                <p style={{ margin: 0, fontSize: '0.78rem', color: '#991b1b', lineHeight: 1.4 }}>
                  Completely blocks user logins with an administrative payment settlement screen until account balance is cleared.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Module Toggles */}
      <div className="saas-card">
        <div className="saas-card-header">
          <div>
            <h3 style={{ margin: 0 }}>Granular Service Module Controls</h3>
            <span style={{ fontSize: '0.8125rem', color: '#64748b' }}>
              Selectively enable or restrict clinical subsystems without affecting underlying database records
            </span>
          </div>
          <button className="saas-btn saas-btn-primary" onClick={handleSaveModules} disabled={savingModules}>
            {savingModules ? 'Saving...' : '💾 Save Module Settings'}
          </button>
        </div>
        <div className="saas-card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {AVAILABLE_MODULES.map(m => {
              const isDisabled = disabledModules.includes(m.id);
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: isDisabled ? '#fef2f2' : '#f8fafc',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{ fontSize: '1.75rem' }}>{m.icon}</div>
                    <div>
                      <strong style={{ fontSize: '0.9375rem', color: isDisabled ? '#991b1b' : '#0f172a' }}>
                        {m.name}
                      </strong>
                      <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8125rem', color: '#64748b' }}>
                        {m.desc}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span className={`saas-badge ${isDisabled ? 'saas-badge-suspended' : 'saas-badge-active'}`}>
                      ● {isDisabled ? 'DISABLED' : 'ENABLED'}
                    </span>
                    <button
                      className={`saas-btn ${isDisabled ? 'saas-btn-success' : 'saas-btn-danger'}`}
                      style={{ padding: '0.35rem 0.75rem', fontSize: '0.8125rem' }}
                      onClick={() => handleToggleModule(m.id)}
                    >
                      {isDisabled ? 'Enable Service' : 'Disable Service'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Lock / Unlock Confirmation Modal */}
      {showLockModal && (
        <div className="saas-modal-backdrop" onClick={() => setShowLockModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="saas-modal-header">
              <h3>
                {lockAction === 'lock' ? '🔒 Administrative Lock Tenant' : '🔓 Unlock Tenant Access'}
              </h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowLockModal(false)}>✕</button>
            </div>
            <form onSubmit={handleLockSubmit}>
              <div className="saas-modal-body">
                <div style={{ background: lockAction === 'lock' ? '#fef2f2' : '#f0fdf4', padding: '1rem', borderRadius: '6px', marginBottom: '1.25rem', color: lockAction === 'lock' ? '#b91c1c' : '#15803d', fontSize: '0.875rem' }}>
                  {lockAction === 'lock' ? (
                    <div>
                      <strong>Operational Freeze Warning:</strong><br />
                      Locking tenant <strong>#{selectedTenantId}</strong> immediately blocks user logins, webhooks, and clinical data modifications. Read-only historical audit access remains preserved.
                    </div>
                  ) : (
                    <div>
                      <strong>Access Restoration:</strong><br />
                      Unlocking tenant <strong>#{selectedTenantId}</strong> restores normal authenticated access and clinical workflows immediately.
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Mandatory Operational Reason / Ticket Ref</label>
                  <textarea
                    className="saas-form-input"
                    rows={3}
                    value={lockReason}
                    onChange={(e) => setLockReason(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowLockModal(false)}>
                  Cancel
                </button>
                <button type="submit" className={`saas-btn ${lockAction === 'lock' ? 'saas-btn-danger' : 'saas-btn-success'}`} disabled={submittingLock}>
                  {submittingLock ? 'Processing...' : lockAction === 'lock' ? 'Confirm Administrative Lock' : 'Confirm Unlock'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Safe Offboarding Modal */}
      {showOffboardModal && (
        <div className="saas-modal-backdrop" onClick={() => setShowOffboardModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '550px' }}>
            <div className="saas-modal-header">
              <h3>📦 Safe Tenant Offboarding &amp; Legal Hold</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowOffboardModal(false)}>✕</button>
            </div>
            <form onSubmit={handleOffboardSubmit}>
              <div className="saas-modal-body">
                <div style={{ background: '#fffbeb', border: '1px solid #fef3c7', padding: '1rem', borderRadius: '6px', marginBottom: '1.25rem', color: '#92400e', fontSize: '0.875rem' }}>
                  <strong>Controlled Archival Safeguard:</strong><br />
                  Offboarding does <em>not</em> drop databases or erase patient clinical records. The tenant is de-routed and placed into a statutory retention hold.
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Statutory Retention Hold Period (Days)</label>
                  <input
                    type="number"
                    min="30"
                    max="3650"
                    className="saas-form-input"
                    value={retentionDays}
                    onChange={(e) => setRetentionDays(e.target.value)}
                    required
                  />
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Guarantees regulatory compliance before any cold-storage archival.
                  </span>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Offboarding Reason &amp; Client Authorization Document</label>
                  <textarea
                    className="saas-form-input"
                    rows={3}
                    value={offboardReason}
                    onChange={(e) => setOffboardReason(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowOffboardModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-danger" disabled={submittingOffboard}>
                  {submittingOffboard ? 'Marking Offboarded...' : '📦 Authorize Controlled Offboarding'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasLifecycleAndControls;
